/**
 * Onboarding Step 4 — Go live.
 *
 * 2026-05-22 redesign:
 *   - Wraps with OnboardingShell (stepper + rail)
 *   - Inline "I'll set up WhatsApp later" checkbox inside the connect card
 *   - WhatsAppConnectedCard replaces TestMessageCard for the post-pairing
 *     state (test-message verification dropped per Option A)
 *   - [Back] [Done] bottom row; Done navigates to /success/ (or /skipped/
 *     if checkbox checked)
 *   - Skip on Done click → POST /onboarding/skip {step: 'go_live'} → navigate
 *     to /skipped/
 *
 * Polling /go-live-status stays. Stage transitions:
 *   waba_pairing      → WhatsAppConnectCard (Embedded Signup)
 *   test_message/live → WhatsAppConnectedCard (subscribe + Done unlock)
 */

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft } from 'lucide-react';

import { apiRequest } from '@/lib/api';
import { useAuth } from '@/lib/useAuth';
import { computeRailState } from '@/lib/onboardingState';
import { Button } from '@/components/ui/button';
import { OnboardingShell } from '@/components/onboarding/OnboardingShell';
import { WhatsAppConnectCard } from '@/components/golive/WhatsAppConnectCard';
import { WhatsAppConnectedCard } from '@/components/golive/WhatsAppConnectedCard';

const POLL_INTERVAL_MS = 2500;

interface GoLiveStatus {
  stage: 'waba_pairing' | 'test_message' | 'skipped' | 'live';
  is_live: boolean;
  waba_id: string | null;
  phone_number_id: string | null;
  phone_number: string | null;
}

export function OnboardingGoLivePage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { me } = useAuth();
  const clientId = me?.memberships[0]?.client_id ?? '';

  // Local skip-intent state; commits on Done click via POST /skip.
  const [willSkip, setWillSkip] = useState(false);
  // Subscribe flag from WhatsAppConnectedCard — Done enables when ok.
  const [subscribeOk, setSubscribeOk] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  // "Wrong number? Reconnect": force the pairing card back even though we're
  // already paired. reconnectFromPhoneId snapshots the current number so we can
  // tell when a NEW number has landed and drop back to the connected view.
  const [forceReconnect, setForceReconnect] = useState(false);
  const [reconnectFromPhoneId, setReconnectFromPhoneId] = useState<string | null>(
    null,
  );

  const statusQuery = useQuery({
    queryKey: ['onboarding', 'go-live-status'],
    queryFn: () => apiRequest<GoLiveStatus>('/onboarding/go-live-status'),
    refetchInterval: (q) => {
      const stage = q.state.data?.stage;
      if (stage === 'skipped') return false;
      return POLL_INTERVAL_MS;
    },
    enabled: !!me,
  });

  const status = statusQuery.data;
  const paired = status?.stage === 'test_message' || status?.stage === 'live';

  // Once paired (the number exists), the widget chosen at step 2 auto-activates.
  // Fetch its state so we can show the "it's live" confirmation.
  const widgetQuery = useQuery({
    queryKey: ['client', clientId, 'widget-config'],
    queryFn: () =>
      apiRequest<{ enabled: boolean; number: string }>(
        `/clients/${clientId}/widget-config`,
      ),
    enabled: !!clientId && paired,
  });
  const widgetLive = !!widgetQuery.data?.enabled && !!widgetQuery.data?.number;

  // Defensive redirect if the user landed here with a stale-but-skipped state.
  useEffect(() => {
    if (status?.stage === 'skipped') {
      navigate('/skipped', { replace: true });
    }
  }, [status?.stage, navigate]);

  // Reconnect landed: a different phone_number_id means the re-pair overwrote
  // the old number — return to the connected view (now showing the new one).
  useEffect(() => {
    if (
      forceReconnect &&
      status?.phone_number_id &&
      status.phone_number_id !== reconnectFromPhoneId
    ) {
      setForceReconnect(false);
      setReconnectFromPhoneId(null);
    }
  }, [forceReconnect, status?.phone_number_id, reconnectFromPhoneId]);

  // Done enables when: WhatsApp paired AND subscribe succeeded, OR willSkip checked.
  const canContinue = (paired && subscribeOk) || willSkip;

  async function handleDone() {
    if (!canContinue || submitting) return;
    setSubmitting(true);
    try {
      if (!paired && willSkip) {
        // Commit the skip on the backend.
        await apiRequest('/onboarding/skip', {
          method: 'POST',
          body: { step: 'go_live' },
        });
        await queryClient.invalidateQueries({ queryKey: ['auth', 'me'] });
        navigate('/skipped');
      } else {
        // Paired + subscribed — straight to celebration.
        await queryClient.invalidateQueries({ queryKey: ['auth', 'me'] });
        navigate('/success');
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <OnboardingShell
      current={4}
      title="Connect your WhatsApp"
      subtitle="Last step. About 2 minutes."
      state={computeRailState(me ?? null)}
    >
      <div className="space-y-5">
        {statusQuery.isPending ? (
          <div className="rounded-3xl bg-card shadow-card p-7 sm:p-9">
            <div className="h-5 w-40 animate-pulse rounded bg-muted" />
            <div className="mt-2 h-4 w-64 animate-pulse rounded bg-muted/60" />
          </div>
        ) : paired && !forceReconnect ? (
          <>
            <WhatsAppConnectedCard
              phoneNumber={status?.phone_number ?? null}
              onSubscribeOk={() => setSubscribeOk(true)}
              onReconnect={() => {
                setReconnectFromPhoneId(status?.phone_number_id ?? null);
                setForceReconnect(true);
              }}
            />
            {widgetLive && (
              <div className="flex items-start gap-3 rounded-2xl border border-emerald-500/20 bg-emerald-50 p-4 text-sm">
                <span className="mt-0.5 text-emerald-600">✓</span>
                <span className="text-dark">
                  <strong>Your WhatsApp button is live.</strong> It's now on your
                  site, linked to your number. Customize it anytime in Integrations.
                </span>
              </div>
            )}
          </>
        ) : (
          <WhatsAppConnectCard
            willSkip={willSkip}
            onWillSkipChange={setWillSkip}
          />
        )}

        {/* Back + Done */}
        <div className="pt-2 flex gap-3">
          <a
            href="/onboarding/connect"
            className="inline-flex items-center justify-center gap-1.5 rounded-full border-2 border-dark/10 px-5 py-3.5 text-base font-bold text-muted-foreground hover:bg-bg-tint hover:text-dark transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            Back
          </a>
          <Button
            type="button"
            onClick={handleDone}
            disabled={!canContinue || submitting}
            className={
              canContinue
                ? 'flex-1 rounded-full bg-brand text-white hover:bg-brand-dark py-3.5 text-base font-bold'
                : 'flex-1 rounded-full bg-brand/40 text-white py-3.5 text-base font-bold cursor-not-allowed'
            }
          >
            {submitting ? 'saving…' : 'Done'}
          </Button>
        </div>
      </div>
    </OnboardingShell>
  );
}
